/*                                                                                                                     
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--'

Copyright (c) 2026 Dynamic IOT Data, Inc. All rights reserved.

@author Andrew Craig
*/
import { PublishTopic } from './events';
import { VisionLayout } from './layout';
import { IPubSubSubscriber, PubSubManager } from './pubsub';
import { AssetService } from './service/asset';
import { DeviceService } from './service/device';
import { AssetFolderService } from './service/folder';
import { LatestTelemetryService } from './service/latest';
import { AssetTelemetryService } from './service/telemetry';
import { UserService } from './service/user.service';
import { VisionWindowManager } from './window/manager';
import { WorkspaceDTO, WorkspaceService } from './service/workspace.service';
import { RouteService } from './service/route_service';
import { GeofenceService } from './service/geofence.service';
import { OrganizationService } from './service/organization.service';
import { RockPitService } from './tab/rockpits_tab/rockpit_service';
import { CameraService } from './tab/avl_tab/camera_service';
import { VisionRockingService } from './tab/rocking_tab/rocking.service';
import { VisionSpotRockingTab } from './tab/spot_rocking_tab/spot_rocking';
import { SpotRockingService } from './tab/spot_rocking_tab/spot_rocking.service';
import { TripService } from './service/trip.service';
import { VisionRoadService } from './service/road.service';
import { RolesPermsService } from './roleperm/service/RolesPermsService';
import { SMSService } from './service/sms.service';
import { VisionSecurityService } from './security/security.service';

export function isNumber(value: string): boolean {
    return value.trim() !== '' && !isNaN(Number(value));
}

export function fromLatLngToPoint(latLng: google.maps.LatLng, map: google.maps.Map): { x: number; y: number } {
    const bounds = map.getBounds();
    if (bounds == undefined) {
        return {
            x: 0,
            y: 0,
        };
    }

    const topRight = map.getProjection()?.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = map.getProjection()?.fromLatLngToPoint(bounds.getSouthWest());
    let zoom = map.getZoom();
    if (zoom == undefined) zoom = 1;
    const scale = Math.pow(2, zoom);

    const worldPoint = map.getProjection()?.fromLatLngToPoint(latLng);
    if (!topRight || !bottomLeft || !worldPoint) return { x: 0, y: 0 };

    const x = (worldPoint.x - bottomLeft.x) * scale;
    const y = (worldPoint.y - topRight.y) * scale;

    const mapDiv = map.getDiv().getBoundingClientRect();
    return {
        x: x + mapDiv.left,
        y: y + mapDiv.top,
    };
}

export function formatDate(timestamp: number | string): string {
    const date = new Date(timestamp);
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');
    const year: number = date.getFullYear();
    return `${month}-${day}-${year}`;
}

export function formatDateTimeZone(timestamp: number | string, zone: string) {
    const dateObj = new Date(timestamp);
    const date = dateObj.toLocaleString('en-US', { hour12: true, timeZone: zone }).split(' ');
    const mdy = date[0];

    // We then parse  the mdy into parts
    const mdySplit = mdy.split('/');
    const month = parseInt(mdySplit[0]);
    const day = parseInt(mdySplit[1]);
    const year = parseInt(mdySplit[2]);

    const localTime12 = dateObj.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    // Putting it all together
    const formattedDate = localTime12 + '&nbsp;' + month + '/' + day + '/' + year;
    return formattedDate;
}

export function formatDateTime(timestamp: number | string) {
    const dateObj = new Date(timestamp);
    const date = dateObj.toLocaleString('en-US', { hour12: true, timeZone: 'America/Chicago' }).split(' ');
    const mdy = date[0];

    // We then parse  the mdy into parts
    const mdySplit = mdy.split('/');
    const month = parseInt(mdySplit[0]);
    const day = parseInt(mdySplit[1]);
    const year = parseInt(mdySplit[2]);

    const localTime12 = dateObj.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    // Putting it all together
    const formattedDate = localTime12 + '&nbsp;' + month + '/' + day + '/' + year;
    return formattedDate;
}

/** Main application Entry Point.
 * This is a singleton class that manages the application state and services.
 *
 */
export class VisionApplication implements IPubSubSubscriber {
    private animationFrameId: number | null = null;

    private static instance: VisionApplication;
    private constructor() {
        console.log('***************************** Application is being created! *****************************');
    }
    public static getInstance() {
        if (!VisionApplication.instance) {
            VisionApplication.instance = new VisionApplication();
            VisionApplication.instance.init();
        }
        return VisionApplication.instance;
    }

    public static nameSort(aName: string, bName: string, ord: string) {
        const aint = parseInt(aName);
        const astr = aName;
        let aNaN = false;
        let bNaN = false;

        if (isNaN(aint)) {
            aNaN = true;
        }

        const bint = parseInt(bName);
        const bstr = bName;
        if (isNaN(bint)) {
            bNaN = true;
        }
        if (aNaN && bNaN) {
            return ord == 'asc' ? (astr > bstr ? 1 : -1) : astr > bstr ? -1 : 1;
        } else if (aNaN == true && bNaN == false) {
            return 1;
        } else if (aNaN == false && bNaN == true) {
            return -1;
        } else if (aNaN == false && bNaN == false) {
            return ord == 'asc' ? (aint > bint ? 1 : -1) : aint > bint ? -1 : 1;
        } else {
            return ord == 'asc' ? (aint > bint ? 1 : -1) : aint > bint ? -1 : 1;
        }
    }

    layout!: VisionLayout;
    windowManager!: VisionWindowManager;

    assetFolderService!: AssetFolderService;
    assetService!: AssetService;
    deviceService!: DeviceService;
    latestService!: LatestTelemetryService;
    telemetryService!: AssetTelemetryService;
    userService!: UserService;
    workspaceService!: WorkspaceService;
    routeService!: RouteService;
    geofenceService!: GeofenceService;
    pubsub!: PubSubManager;
    activeWorkspace!: WorkspaceDTO | undefined;
    organizationService!: OrganizationService;
    rockPitService!: RockPitService;
    rockingService!: VisionRockingService;
    spotRockingService!: SpotRockingService;
    cameraService!: CameraService;
    tripService!: TripService;
    roadService!: VisionRoadService;
    //rolesPermsService!: RolesPermsService;
    securityService!: VisionSecurityService;
    smsService!: SMSService;

    identifier = '9412140a-9b55-4b90-85e4-22f98af60b2c';

    init() {
        this.pubsub = new PubSubManager();

        google.charts
            .load('current', {
                packages: ['corechart', 'table'],
            })
            .then(() => {
                this.userService = new UserService(this);
                this.organizationService = new OrganizationService();
                this.assetService = new AssetService();
                this.telemetryService = new AssetTelemetryService(this);
                this.assetFolderService = new AssetFolderService();
                this.deviceService = new DeviceService();
                this.latestService = new LatestTelemetryService(this);
                this.geofenceService = new GeofenceService();
                this.workspaceService = new WorkspaceService(this.pubsub);
                this.rockPitService = new RockPitService();
                this.cameraService = new CameraService();
                this.rockingService = new VisionRockingService();
                this.spotRockingService = new SpotRockingService();
                this.tripService = new TripService();
                this.roadService = new VisionRoadService();
                //this.rolesPermsService = new RolesPermsService();
                this.securityService = new VisionSecurityService();
                this.smsService = new SMSService();

                this.loadServices();
            });
    }

    public setMapWorkspace(map: google.maps.Map) {
        const selected = localStorage.getItem('selected_workspace');
        if (selected) {
            const spaceInfo = this.workspaceService.workspaces.get(selected);
            if (spaceInfo && spaceInfo.config.mapType) {
                map.setMapTypeId(spaceInfo.config.mapType);
            }
        }
    }

    async loadServices() {
        this.pubsub.subscribe(PublishTopic.AVL_MAP_LOADED, this);

        await this.userService.init();
        //await this.rolesPermsService.init();
        await this.securityService.init();
        await this.assetService.init();
        await this.telemetryService.init();
        await this.assetFolderService.init();
        await this.deviceService.init();
        await this.latestService.init();
        await this.geofenceService.init();
        await this.organizationService.init();
        await this.workspaceService.init();
        await this.rockPitService.init();
        await this.cameraService.init();
        await this.rockingService.init();
        await this.spotRockingService.init();
        await this.tripService.init();
        await this.roadService.init();

        await this.smsService.init();

        this.layout = new VisionLayout(this);
        this.windowManager = new VisionWindowManager();
        this.layout.init();

        //this.latestService.tick();
    }

    saveUserSettings(): unknown {
        const settings = {};
        return settings;
    }

    notify(msg: string): void {
        switch (msg) {
            case PublishTopic.WORKSPACES_LOADED: {
                this.loadServices();
                break;
            }
            case PublishTopic.AVL_MAP_LOADED: {
                this.latestService.tick();
                break;
            }
        }
    }

    /** Convert miles to km.
     * Will return 0 if no number provided.
     */
    public km2miles(km: number): number {
        const res = km * 0.621371;
        if (isNaN(res)) {
            return 0.0;
        } else {
            return res;
        }
    }

    public miles2km(miles: number): number {
        const res = miles * (1 / 0.621371);
        if (isNaN(res)) {
            return 0.0;
        } else {
            return res;
        }
    }

    private startDeadReckoning(): void {
        const animate = (): void => {
            // const position = this.reckoner.getPosition();

            // this.marker.setPosition({
            //     lat: position.lat,
            //     lng: position.lng,
            // });
            this.animationFrameId = requestAnimationFrame(animate);
        };

        this.animationFrameId = requestAnimationFrame(animate);
    }
}
